import { isCreatableAction } from '@atlassian/conversation-assistant-action-helpers/utils';
import {
	isConfluenceCreateNewAction,
	type PreviewCardAction,
} from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import type { ExperienceTrackerAPI } from '@atlassian/conversation-assistant-instrumentation/main';
import { ASK_QUESTION_KEY } from '@atlassian/conversation-assistant-message-actions-api/constants';

import { abortPreviewCardRenderExperience } from '../abort-preview-card-render-experience';
import { abortGenerateContentChildExperiences } from './abort-generate-content-child-experiences';

export const AIFC_ASK_QUESTION_ABORT_REASON =
	'ask_question - awaiting user response, cannot bound time';
export const AIFC_NON_CONFLUENCE_ACTION_ABORT_REASON = 'non-confluence AIFC action returned';

type HandleAifcResponseActionsParams = {
	experienceTracker: ExperienceTrackerAPI;
	actions: ReadonlyArray<{ key: string }> | undefined;
	attributes: Record<string, unknown>;
};

/**
 * Handles AIFC_GENERATE_CONTENT for the actions returned on an AIFC response:
 * - `creating`: a Confluence AIFC action was returned, so the experience keeps running.
 * - `aborted`: ask_question (can't be time-bound; restarted when answered) or a non-Confluence
 *   AIFC action (not tracked by this experience) was returned, so the experience is aborted.
 * - `no-action`: no AIFC action was returned. Callers decide how to fail the experience.
 */
export const handleAifcResponseActions = ({
	experienceTracker,
	actions = [],
	attributes,
}: HandleAifcResponseActionsParams): 'creating' | 'aborted' | 'no-action' => {
	const hasConfluenceAifcAction = actions.some((action) =>
		isConfluenceCreateNewAction(action as PreviewCardAction),
	);
	const hasAskQuestionAction = actions.some((action) => action.key === ASK_QUESTION_KEY);
	const hasOtherAifcAction = actions.some((action) => isCreatableAction(action));

	if (hasConfluenceAifcAction) {
		return 'creating';
	}

	if (!hasAskQuestionAction && !hasOtherAifcAction) {
		return 'no-action';
	}

	const reason = hasAskQuestionAction
		? AIFC_ASK_QUESTION_ABORT_REASON
		: AIFC_NON_CONFLUENCE_ACTION_ABORT_REASON;

	experienceTracker.abort({
		name: ExperienceName.AIFC_GENERATE_CONTENT,
		reason,
		attributes: {
			...attributes,
			aifcResponseActionClassification: hasAskQuestionAction ? 'ask-question' : 'other-aifc',
			aifcResponseActionKeys: actions.map((action) => action.key),
		},
	});

	abortGenerateContentChildExperiences(experienceTracker, reason, attributes);

	if (hasAskQuestionAction) {
		abortPreviewCardRenderExperience({
			experienceTracker,
			reason,
			terminalReason: 'awaiting-disambiguation',
			attributes,
		});
	}

	return 'aborted';
};
